// Base component (not a leaf theme's override): the donut chart's behaviour lives at
// the base tier so every leaf theme inherits it. A theme that draws the ring with a
// chart library of its own extends this module and replaces highlight() (how the
// selected element is shown) and isSelectionClick() (what counts as a click on the
// chart), keeping the selection logic below.
const component = require('../component/component');

// Donut chart selection. A legend line (a button) and its ring segment carry the same
// data-zit-chart-index. Clicking either selects that element: the centre shows
// "value / total" and the element is marked active. Clicking the selected element
// again, clicking anywhere that is not a selection target, or pressing Escape inside
// the chart shows the total again. Pure DOM behaviour, no CSS-framework dependency.
//
// While an element is selected the root carries data-zit-chart-selected="true", so a
// theme can dim the other segments.
const donutChart = Object.assign({}, component, {
    subelements: {
        centerValue: null,
        centerTotal: null,
    },
    total: 0,               // read from data-zit-total
    selectedIndex: null,    // the selected element's index; null when none is

    init: function () {
        component.init.call(this);

        this.wire();
    },

    wire: function () {
        // one listener for every legend line and segment of this chart
        this.element.addEventListener('click', (event) => {
            let target = event.target instanceof Element ? event.target.closest('[data-zit-chart-index]') : null;

            if (target === null || !this.element.contains(target)) {
                return;
            }

            let index = parseInt(target.dataset.zitChartIndex, 10);

            // a second click on the selected element shows the total again
            if (index === this.selectedIndex) {
                this.deselect();
            } else {
                this.select(index);
            }
        });

        // a click anywhere else on the page shows the total again. It runs after the listener above (the event
        // bubbles up to the document last), which is why it ignores the clicks that one handles
        document.addEventListener('click', (event) => {
            if (this.selectedIndex !== null && !this.isSelectionClick(event.target)) {
                this.deselect();
            }
        });

        // so does Escape, from a legend line that has the focus
        this.element.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.selectedIndex !== null) {
                this.deselect();
            }
        });
    },

    // Whether a click landed on something that selects an element of this chart: a legend line or a segment.
    // A theme drawing its own ring adds the ring's own segments here.
    isSelectionClick: function (target) {
        if (!(target instanceof Element)) {
            return false;
        }

        let selectionTarget = target.closest('[data-zit-chart-index]');

        return selectionTarget !== null && this.element.contains(selectionTarget);
    },

    select: function (index) {
        this.selectedIndex = index;
        this.showCenter(index);
        this.highlight(index);
    },

    deselect: function () {
        this.selectedIndex = null;
        this.showCenter(null);
        this.highlight(null);
    },

    // The value an element stands for, as its legend line carries it
    elementValue: function (index) {
        let legendItem = this.element.querySelector('button[data-zit-chart-index="' + index + '"]');

        return legendItem !== null ? legendItem.dataset.zitChartValue : 0;
    },

    // The centre: the total alone, or the selected element's value followed by "/ total"
    showCenter: function (index) {
        if (!this.hasSubelement('centerValue')) {
            return;
        }

        if (index === null) {
            this.subelements.centerValue.textContent = this.total;
            this.hideSub('centerTotal');
        } else {
            this.subelements.centerValue.textContent = this.elementValue(index);
            this.showSub('centerTotal');
        }
    },

    // Marks the selected element's legend line and segment active (the theme's active state class) and the
    // others not; null clears them all. The legend lines also tell assistive technology which one is pressed.
    highlight: function (index) {
        let activeTokens = this.hm.behaviors.activation.stateClass('active').split(' ').filter((token) => token !== '');
        let items = this.element.querySelectorAll('[data-zit-chart-index]');

        items.forEach((item) => {
            let isActive = index !== null && parseInt(item.dataset.zitChartIndex, 10) === index;

            if (isActive) {
                item.classList.add(...activeTokens);
            } else {
                item.classList.remove(...activeTokens);
            }

            if (item.tagName === 'BUTTON') {
                item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            }
        });

        this.element.dataset.zitChartSelected = index === null ? 'false' : 'true';
    },
});

module.exports = donutChart;
